import * as React from "react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import uniqueId from "lodash.uniqueid";
import { useTextContent } from "@sproutsocial/seeds-react-hooks";
import { innerText } from "./utils/innertext";
import { disabled } from "@sproutsocial/seeds-react-mixins";
import Box, { TypeBoxProps } from "@sproutsocial/seeds-react-box";
import Button from "@sproutsocial/seeds-react-button";
import Checkbox from "@sproutsocial/seeds-react-checkbox";
import Icon from "@sproutsocial/seeds-react-icon";
import Input, { TypeInputProps } from "@sproutsocial/seeds-react-input";
import Popout from "@sproutsocial/seeds-react-popout";
import Radio from "@sproutsocial/seeds-react-radio";
import Switch from "@sproutsocial/seeds-react-switch";
import Text from "@sproutsocial/seeds-react-text";
import type {
  TypeMenuButtonProps,
  TypeMenuGroupProps,
  TypeMenuItemProps,
  TypeMenuItemRoles,
  TypeMenuProps,
  TypeMenuRoles,
  TypeMenuSwitchProps,
} from "./MenuTypes";
import {
  MENU_ITEM_ROLES,
  MENU_ROLES,
  DescendantProvider,
  useDescendant,
  useDescendants,
  MenuButtonContext,
  MenuContext,
  MenuDescendantContext,
  useMenuKeyDown,
} from "./utils";
import type { TypeMenuDescendantProps } from "./utils";
import { MenuItemContainer, MenuItemsContainer } from "./styles";

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const getMenuItemRole = (
  menuRole?: TypeMenuRoles | null
): TypeMenuItemRoles => {
  switch (menuRole) {
    case MENU_ROLES.LIST:
      return MENU_ITEM_ROLES.LISTITEM;

    case MENU_ROLES.LISTBOX:
      return MENU_ITEM_ROLES.OPTION;

    case MENU_ROLES.MENU:
    default:
      return MENU_ITEM_ROLES.MENUITEM;
  }
};

function isValueSelected(menuValue, value) {
  if (!menuValue) return false;

  if (typeof menuValue === "string") {
    return Boolean(menuValue && value && menuValue === value);
  } else if (Array.isArray(menuValue)) {
    return menuValue.includes(value);
  }

  return false;
}

/**
 * @deprecated MenuItem is deprecated. MenuItem will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export const MenuItem = ({
  id: idProp,
  index: indexProp,
  as,
  children,
  role: roleProp,
  elemBefore,
  elemAfter,
  value: valueProp,
  onKeyPress,
  onClick,
  selected,
  disabled,
  indeterminate,
  label: labelProp,
  active: activeProp,
  ...props
}: TypeMenuItemProps) => {
  const {
    role: menuRole,
    onChange,
    value,
    multiselect,
    state: menuState,
    selectCallbacks,
    menuRef,
  } = useContext(MenuContext);
  const { selectionIndex, filteredItems } = menuState;
  const { closePopout, closeOnItemClick } = useContext(MenuButtonContext);
  const isSelected = selected || isValueSelected(value, valueProp);
  const shouldClosePopout = closeOnItemClick && !multiselect;
  const itemRole = roleProp || getMenuItemRole(menuRole);
  const id = useMemo(() => idProp || uniqueId("MenuItem-"), [idProp]);
  const textContent = useTextContent("");
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const label = labelProp || textContent.current;
  const ref = useRef<HTMLDivElement | null>(null);
  const index = useDescendant<HTMLElement, TypeMenuDescendantProps>(
    {
      context: MenuDescendantContext,
      element: ref.current,
      value: valueProp || label,
      label,
      disabled,
      id,
    },
    indexProp
  );
  const isHidden = filteredItems && !filteredItems.includes(index);
  const isFocused = index === selectionIndex;
  selectCallbacks.current[index] = onClick;
  const [keyPressed, setKeyPressed] = useState(false);
  const isActive = useMemo(
    () => activeProp || (isFocused && keyPressed),
    [activeProp, isFocused, keyPressed]
  );
  const isCheckboxOrRadio = [
    MENU_ITEM_ROLES.CHECKBOX,
    MENU_ITEM_ROLES.RADIO,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ].includes(itemRole as any); // @ts-notes @refactor - since we're only really checking against the string I think we can safely leave this for refactor, nothing else worked for me
  const interactive =
    onClick ||
    onKeyPress ||
    [
      MENU_ITEM_ROLES.OPTION,
      MENU_ITEM_ROLES.MENUITEM,
      MENU_ITEM_ROLES.CHECKBOX,
      MENU_ITEM_ROLES.RADIO,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ].includes(itemRole as any);

  /**
   * The Checkbox/Radio component doesn't expose a tabIndex prop, so we can't set the tabIndex on the underlying input
   * to remove it from the tab order. Resulting to DOM manipulation as a last resort. We can remove this if Checkbox
   * exposes this prop in the future.
   */
  useEffect(() => {
    if (isCheckboxOrRadio && ref.current) {
      const input = ref.current.querySelector("input");

      if (input) {
        input.tabIndex = -1;
      }
    }
  }, [ref, isCheckboxOrRadio]);
  useEffect(() => {
    if (filteredItems) {
      setKeyPressed(true);
    } else if (selectionIndex !== 0) {
      setKeyPressed(true);
    }
  }, [selectionIndex, filteredItems]);
  const handleClick = useCallback(
    (e: React.SyntheticEvent<HTMLButtonElement>) => {
      menuRef.current?.focus();

      if (onChange && itemRole !== MENU_ITEM_ROLES.LISTITEM) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onChange((valueProp as any) || label);
      }

      shouldClosePopout && closePopout && closePopout();
      onClick && onClick(e);
    },
    [
      menuRef,
      onChange,
      itemRole,
      shouldClosePopout,
      closePopout,
      onClick,
      valueProp,
      label,
    ]
  );
  const before = useMemo(() => {
    if (elemBefore) return elemBefore;

    if (itemRole === MENU_ITEM_ROLES.CHECKBOX) {
      return (
        <Checkbox
          id={`Checkbox-${id}`}
          checked={isSelected}
          aria-hidden="true"
          onChange={noop}
          indeterminate={indeterminate}
          mr={300}
        />
      );
    } else if (itemRole === MENU_ITEM_ROLES.RADIO) {
      return (
        <Radio
          id={`Radio-${id}`}
          name={`Radio-${id}`}
          checked={isSelected}
          aria-hidden="true"
          onChange={noop}
          mr={300}
        />
      );
    }
  }, [elemBefore, id, indeterminate, isSelected, itemRole]);
  const after = useMemo(() => {
    if (elemAfter) return elemAfter;

    if (isSelected && !isCheckboxOrRadio) {
      return <Icon name="check-solid" aria-hidden size="small" ml={300} />;
    }
  }, [elemAfter, isCheckboxOrRadio, isSelected]);
  return (
    <MenuItemContainer
      data-qa-menu-item={label}
      {...props}
      active={isActive}
      data-qa-is-active={isActive}
      id={id}
      aria-label={labelProp}
      value={valueProp}
      data-value={valueProp}
      forwardedAs={as || "li"}
      aria-checked={isCheckboxOrRadio ? isSelected : undefined}
      aria-selected={
        itemRole === MENU_ITEM_ROLES.OPTION ? isSelected : undefined
      }
      disabled={disabled}
      aria-disabled={disabled}
      isCheckboxOrRadio={isCheckboxOrRadio}
      hidden={Boolean(isHidden)} // @ts migration note: coercing to a boolean here otherwise it thinks its actually receiving callbacks
      interactive={Boolean(interactive)} // @ts migration note: coercing to a boolean here otherwise it thinks its actually receiving callbacks
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore handleclick takes in different event type than what it receives half the times
      onClick={!disabled ? handleClick : undefined}
      role={itemRole}
      selected={isSelected}
      tabIndex={
        itemRole !== MENU_ITEM_ROLES.LISTITEM && !disabled ? -1 : undefined
      }
    >
      <div
        ref={ref}
        style={{
          display: "flex",
          alignItems: props.alignItems || "center",
          justifyContent: "space-between",
        }}
      >
        {before}
        <div
          style={{
            flexGrow: 1,
            wordBreak: "break-word",
            minWidth: 0,
          }}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore some legacy ref type mismatch between divs
          ref={textContent}
        >
          {children}
        </div>
        {after}
      </div>
    </MenuItemContainer>
  );
};

/**
 * @deprecated MenuCheckbox is deprecated. MenuCheckbox will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export const MenuCheckbox = (props: TypeMenuItemProps) => (
  <MenuItem role={MENU_ITEM_ROLES.CHECKBOX} {...props} />
);

/**
 * @deprecated MenuRadio is deprecated. MenuRadio will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export const MenuRadio = (props: TypeMenuItemProps) => (
  <MenuItem role={MENU_ITEM_ROLES.RADIO} {...props} />
);

/**
 * @deprecated MenuSwitch is deprecated. MenuSwitch will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export const MenuSwitch = (props: TypeMenuSwitchProps) => {
  const { value: menuValue } = useContext(MenuContext);
  const {
    placement,
    labelProps = {},
    switchProps = {},
    children,
    id,
    label,
    ...rest
  } = props;
  const menuItemId = useMemo(() => id || uniqueId("MenuItem-"), [id]);
  const labelId = `${menuItemId}-label`;
  const switchElement = (
    <Switch
      id={`${menuItemId}-switch`}
      checked={props.selected || isValueSelected(menuValue, props.value)}
      aria-hidden="true"
      tabIndex={-1}
      {...(placement === "before" ? { mr: 300 } : { ml: 300 })}
      {...switchProps}
      // This prop is passed after switchProps to disallow overrides.
      onClick={noop}
    />
  );
  const menuItemProps = {
    id: menuItemId,
    label,
    elemBefore: placement === "before" ? switchElement : undefined,
    // Empty fragment prevents a check icon in elemAfter when the switch is checked. Can be overridden.
    elemAfter: placement === "before" ? <></> : switchElement,
    ...rest,
  };
  return (
    <MenuItem {...menuItemProps}>
      {/*Using Text and aria-labelledby instead of Label to avoid default Label styling and onClick behavior.*/}
      <Text
        id={labelId}
        {...labelProps}
        // This prop is passed after labelProps to disallow overrides.
        // Fall back to the label prop if children is falsy.
        // eslint-disable-next-line react/no-children-prop
        children={children || label}
      />
    </MenuItem>
  );
};

const MenuTitle = styled(Text)`
  font-weight: 600;
  padding-left: ${({ theme }) => theme.space[300]};
  padding-top: ${({ theme }) => theme.space[200]};
  padding-bottom: ${({ theme }) => theme.space[300]};
  color: ${({ theme }) => theme.colors.text.headline};
`;

/**
 * @deprecated MenuGroup is deprecated. MenuGroup will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export const MenuGroup = ({
  children,
  title,
  titleAs,
  disabled: isDisabled = false,
  ...props
}: TypeMenuGroupProps) => {
  const menuTitleId = useMemo(() => uniqueId("menu-title-"), []);
  return (
    <>
      <Box
        p={300}
        role="group"
        aria-labelledby={title ? menuTitleId : undefined}
        borderTop="1px solid"
        borderColor="container.border.base"
        {...props}
      >
        {title && (
          <MenuTitle
            id={menuTitleId}
            role="presentation"
            fontSize={200}
            forwardedAs={titleAs || "li"}
            css={isDisabled ? disabled : undefined}
          >
            {title}
          </MenuTitle>
        )}
        {children}
      </Box>
    </>
  );
};

/**
 * @deprecated MenuDivider is deprecated. MenuDivider will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export const MenuDivider = (props: TypeBoxProps) => (
  <Box
    as="li"
    role="separator"
    borderTop="1px solid"
    borderColor="container.border.base"
    {...props}
  />
);

/**
 * @deprecated MenuFilterInput is deprecated. MenuFilterInput will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
const MenuFilterInput = ({ type = "search", ...props }: TypeInputProps) => {
  const { onChange, onFocus, onBlur } = props;
  const { state, setState } = useContext(MenuContext);
  const handleOnChange: TypeInputProps["onChange"] = useCallback(
    (event, value) => {
      onChange && onChange(event, value);
      setState({
        filterQuery: value,
      });
    },
    [setState, onChange]
  );
  const handleOnFocus: TypeInputProps["onFocus"] = useCallback(
    (event) => {
      onFocus && onFocus(event);
      setState({
        isFilterInputFocused: true,
      });
    },
    [setState, onFocus]
  );
  const handleOnBlur: TypeInputProps["onBlur"] = useCallback(
    (event) => {
      onBlur && onBlur(event);
      setState({
        isFilterInputFocused: false,
      });
    },
    [setState, onBlur]
  );
  return (
    <Input
      {...props}
      type={type}
      value={state.filterQuery}
      onChange={handleOnChange}
      onFocus={handleOnFocus}
      onBlur={handleOnBlur}
    />
  );
};

/**
 * @deprecated MenuItems are deprecated. MenuItems will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
const MenuItems = ({
  children,
  role,
  multiselect,
  innerRef,
  ...props
}: TypeMenuProps) => {
  const { handleKeyDown, activeDescendent } = useMenuKeyDown();
  return (
    <MenuItemsContainer
      {...props}
      forwardedAs="ul"
      ref={innerRef}
      role={role}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      aria-activedescendant={activeDescendent?.id}
      aria-multiselectable={multiselect ? true : undefined}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      margin={0}
      padding={0}
      overflow="hidden"
    >
      {children}
    </MenuItemsContainer>
  );
};

/**
 * @deprecated MenuFooter is deprecated. MenuFooter will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
const MenuFooter = ({ children }) => {
  const { setState } = useContext(MenuContext);
  const handleOnBlur = useCallback(() => {
    setState({
      isFooterFocused: false,
    });
  }, [setState]);
  const handleOnFocus = useCallback(() => {
    setState({
      isFooterFocused: true,
    });
  }, [setState]);
  return (
    <Box onBlur={handleOnBlur} onFocus={handleOnFocus}>
      {children}
    </Box>
  );
};

/**
 * @deprecated Menu is deprecated. Menu will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export const Menu = ({
  role = MENU_ROLES.MENU,
  children,
  onChange,
  value,
  multiselect,
  innerRef,
  footerContent,
  ...props
}: TypeMenuProps) => {
  const [descendants, setDescendants] = useDescendants<
    HTMLElement,
    TypeMenuDescendantProps
  >();
  const [state, setState] = useState({
    selectionIndex: 0,
    filterQuery: "",
    filteredItems: null,
    isFilterInputFocused: false,
    isFooterFocused: false,
  });
  const selectCallbacks = useRef<((() => void) | null | undefined)[]>([]);
  const menuRef = useRef(null);
  const setContextState = useCallback(
    (newState) => {
      setState({ ...state, ...newState });
    },
    [state]
  );
  return (
    <DescendantProvider
      context={MenuDescendantContext}
      items={descendants}
      set={setDescendants}
    >
      <MenuContext.Provider
        value={{
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          menuRef: innerRef || menuRef,
          role,
          onChange,
          value,
          multiselect,
          state,
          setState: setContextState,
          selectCallbacks,
        }}
      >
        <MenuItems
          {...props}
          role={role}
          multiselect={multiselect}
          innerRef={menuRef}
        >
          {children}
        </MenuItems>
        {footerContent ? <MenuFooter>{footerContent}</MenuFooter> : null}
      </MenuContext.Provider>
    </DescendantProvider>
  );
};

MenuGroup.displayName = "Menu.Group";
MenuItem.displayName = "Menu.Item";
MenuCheckbox.displayName = "Menu.Checkbox";
MenuRadio.displayName = "Menu.Radio";
MenuSwitch.displayName = "Menu.Switch";
MenuDivider.displayName = "Menu.Divider";
MenuFilterInput.displayName = "Menu.FilterInput";
Menu.displayName = "Menu";

Menu.Group = MenuGroup;
Menu.Item = MenuItem;
Menu.Checkbox = MenuCheckbox;
Menu.Radio = MenuRadio;
Menu.Switch = MenuSwitch;
Menu.Divider = MenuDivider;
Menu.FilterInput = MenuFilterInput;

const CustomPopoutContent = styled(Popout.Content)`
  padding: 0;
  margin-left: 0;
  margin-right: 0;
`;

/**
 * @deprecated MenuButton is deprecated. MenuButton will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export const MenuButton = ({
  content,
  popoutProps,
  children,
  onClick,
  closeOnItemClick = true,
  id = uniqueId("MenuButton-"),
  placement = "bottom",
  isOpen,
  setIsOpen,
  ...props
}: TypeMenuButtonProps) => {
  const [isInternalShown, setIsInternalShown] = useState(false);
  const isControlled = typeof isOpen === "boolean";
  const isShown = isControlled ? isOpen : isInternalShown;
  const setIsShown = useMemo(
    () =>
      isControlled && setIsOpen
        ? setIsOpen
        : (nextIsShown) => setIsInternalShown(nextIsShown),
    [isControlled, setIsOpen]
  );
  const closePopout = useCallback(() => setIsShown(false), [setIsShown]);
  const ariaLabelledBy = props["aria-labelledby"]
    ? `${props["aria-labelledby"]} ${id}`
    : undefined;
  const ariaLabel = props["aria-label"]
    ? `${props["aria-label"]} ${innerText(children)}`
    : undefined;
  return (
    <MenuButtonContext.Provider
      value={{
        closePopout,
        closeOnItemClick,
      }}
    >
      <Popout
        isOpen={isShown}
        setIsOpen={setIsShown}
        content={<CustomPopoutContent>{content}</CustomPopoutContent>}
        placement={placement}
        {...popoutProps}
        width={props.width}
      >
        <Button
          {...props}
          id={id}
          aria-labelledby={ariaLabelledBy?.trim()}
          aria-label={ariaLabel?.trim()}
          active={isShown}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onClick && onClick(e as any); // @ts-notes unfortunately we're passing in a keyboard event here and not the mouse event it expects, so no way to coerce it
              setIsShown(true);
            }
          }}
          onClick={(e) => {
            onClick && onClick(e);
            setIsShown(!isShown);
          }}
        >
          {children}
        </Button>
      </Popout>
    </MenuButtonContext.Provider>
  );
};

/**
 * @deprecated Menu is deprecated. Menu will be removed in 2025 in favor of seeds-react-menu ecosystem. Create a plan to migrate to {@link https://github.com/sproutsocial/seeds/tree/main/seeds-react/seeds-react-menu/src | seeds-react-menu}.
 */
export default Menu;
